import React, { useContext, useEffect, Suspense, useLayoutEffect } from "react";
import {
  CssBaseline,
  styled,
  ThemeProvider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Routes, Route, useLocation, useSearchParams } from "react-router-dom";
import { routes as appRoutes } from "./routes";
import Layout from "./components/Layout";
import { AuthProvider } from "./services/authentication/auth.context";
import { useRouter } from "./utils/hooks/useRouter";
import theme from "./themes/theme";
import {
  analytics,
  createFirebaseAnalytic,
} from "./services/analytics/firebase.service";
import { initKva } from "./services/analytics/kva.service";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Provider } from "react-redux";
import { makeStore } from "./store/store";
import "./assets/css/style.css";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import LoadingFullPage from "./components/Static/Loading/LoadingFullPage";
import MuiToast from "./components/Static/Toast/MuiToast";
import jwt_decode from "jwt-decode";
import Loading from "./components/Static/Loading/Loading";
import ImageGif from "./components/AdminMerchant/RetailWidget/Onboarding/ImageGif";
import { BOOKING_HOTEL_PLATFORM, BOOKING_PLATFORM } from "./utils/constant";
import { I18nextProvider } from "react-i18next";
import i18n from "./utils/i18n";
import moment from "moment";
import { Theme as KvwebTheme } from "kvweb-ui";
import { useCustomColor } from "./utils/overrides/color";


const StyledContainer = styled("div")(() => ({
  "@media (max-width: 600px)": {
    ".Toastify__toast": {
      width: "343px",
      borderRadius: "12px !important",
      marginBottom: "16px",
      background: "rgba(35,36, 42,0.9)",
      boxShadow:
        "0px 6px 24px 4px rgba(0, 0, 0, 0.05), 0px 2px 10px rgba(0, 0, 0, 0.15)",
      minHeight: "48px",
      bottom: "50px",
    },
    ".Toastify__toast-container": {
      textAlign: "left",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    ".Toastify__toast-container--bottom-center": {
      bottom: "60px",
    },
    ".Toastify__toast-body": {
      padding: 0,
    },
  },
}));

function App() {
  const router = useRouter();
  const { pathname } = useLocation();
  const [isKeiviState, setIsKeiviState] = React.useState(() => {
    return router?.query?.is_keivi || localStorage.getItem("isKeivi");
  });
  const isKeivi = isKeiviState;
  // Sử dụng hook để trigger re-render khi CustomColor thay đổi
  const customColor = useCustomColor();

  // Set language based on query parameter or default to 'vi'
  if(router.query.lang){
    i18n.changeLanguage(router.query.lang as string);
    moment.locale(router.query.lang as string);
  }

  const mode = "light";
  const themeData = theme(mode, {
    isKeivi: isKeivi === "true",
    isFromPageBuilder: pathname.includes('/preview/') || pathname.includes('/preview-v2/'),
  });

  useEffect(() => {
    if (window.location.search.includes("is_keivi=true") && !localStorage.getItem("isKeivi")) {
      localStorage.setItem("isKeivi", "true");
      setIsKeiviState("true");
    } else {
      if (!window.location.search.includes("is_keivi=true") && !!localStorage.getItem("isKeivi")) {
        localStorage.removeItem("isKeivi");
        // Cập nhật state để trigger re-render thay vì reload trang
        setIsKeiviState(null);
      }
    }
  }, [customColor]); // Thêm customColor vào dependency để trigger re-render khi CustomColor thay đổi

  function getPlatformByUrl() {
    if (router.query.platform) {
      return router.query.platform;
    }
    let platform = "fnb";
    if (pathname.includes("retail")) {
      platform = "retail";
    }
    if (pathname.includes("kiotonline")) {
      platform = "kiotonline";
    }
    if (pathname.includes(BOOKING_PLATFORM)) {
      platform = "booking";
    }
    if (pathname.includes(BOOKING_HOTEL_PLATFORM)) {
      platform = "hotel";
    }
    return platform;
  }

  // analytics
  useEffect(() => {
    const platform = getPlatformByUrl();
    let retailerId = "";
    if (
      platform === "retail" ||
      platform === BOOKING_PLATFORM ||
      platform === BOOKING_HOTEL_PLATFORM
    ) {
      const token = router.query.code;
      if (token) {
        try {
          const decodeToken: {
            kvrid: number;
          } = jwt_decode(token);
          if (
            typeof decodeToken !== "undefined" &&
            typeof decodeToken.kvrid !== "undefined"
          ) {
            retailerId = decodeToken.kvrid.toString();
          }
        } catch (e) {
          console.log("=============Error decode token");
          console.error(e);
        }
      }
    }
    if (window && !analytics) {
      createFirebaseAnalytic(platform, retailerId);
    }
  }, [router]);
  useEffect(() => {
    initKva();
    KvwebTheme(mode).palette.primary.main = "#FF5C0A";
    // @ts-ignore
    KvwebTheme(mode).components.MuiButton.styleOverrides.root = {
      backgroundColor: "#FF5C0A"
    }
  }, [KvwebTheme]);
  // useEffect(() => {
  //   moment.locale(i18n.language)
  // }, [i18n.language]);
  return (
    <I18nextProvider i18n={i18n}>
      <ThemeProvider theme={themeData}>
        <Provider store={makeStore}>
          <Suspense fallback={<Loading />}>
            <AuthProvider>
              <CssBaseline />
              <Layout>
                <Routes>
                  {appRoutes.map((route) => (
                    <Route
                      key={route.key}
                      path={route.path}
                      element={<route.component />}
                    />
                  ))}
                </Routes>
                <StyledContainer>
                  <ToastContainer />
                </StyledContainer>
                <LoadingFullPage />
                <MuiToast />
              </Layout>
            </AuthProvider>
          </Suspense>
        </Provider>
      </ThemeProvider>
    </I18nextProvider>
  );
}

export default App;
